import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiArrowUpRight, FiStar, FiCode, FiLayout, FiSmartphone, FiPackage, FiCpu, FiFeather } from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import ProjectCard from '../components/ProjectCard';

// FRIEND'S STARTUP WEBSITE - UPDATE THIS URL
const FRIEND_WEBSITE_URL = 'https://your-friends-startup.com';

const services = [
  { icon: <FiCode size={24} />, title: 'Web Development', desc: 'Blazing-fast, scalable websites built with modern stacks and pixel-perfect precision.' },
  { icon: <FiSmartphone size={24} />, title: 'Web App Development', desc: 'Complex web applications with intuitive UX, real-time features, and robust architecture.' },
  { icon: <FiLayout size={24} />, title: 'UI/UX Design', desc: 'Human-centered design that balances beauty and function to drive engagement and conversions.' },
  { icon: <FiFeather size={24} />, title: 'Branding', desc: 'Distinctive brand identities that communicate your values and resonate with your audience.', externalLink: FRIEND_WEBSITE_URL },
  { icon: <FiCpu size={24} />, title: 'Software Development', desc: 'Custom software solutions engineered for performance, reliability, and long-term scale.' },
  { icon: <FiPackage size={24} />, title: 'Digital Strategy', desc: 'Data-driven strategies that align your digital presence with real business outcomes.' },
];

const testimonials = [
  { name: 'Alexandra Chen', role: 'CEO, Luminary Co.', text: ' SitePixel transformed our digital presence completely. The attention to detail and the quality of delivery is unmatched. Our conversion rate improved by 340% after the redesign.', stars: 5 },
  { name: 'Marcus Hartley', role: 'Founder, Aura SaaS', text: 'Working with  SitePixel felt like having a world-class design studio in our corner. They understood our vision immediately and elevated it beyond what we imagined.', stars: 5 },
  { name: 'Priya Nair', role: 'CMO, Velorum', text: 'The brand identity they created for us is stunning. Every asset, every touchpoint feels premium and intentional. Truly exceptional work from start to finish.', stars: 5 },
];

const stats = [
  { value: '25+', label: 'Projects Delivered' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '2+', label: 'Years of Excellence' },
  { value: '10+', label: 'Global Clients' },
];

const HomePage = () => {
  const { projects } = useApp();
  const featured = projects.filter(p => p.featured).slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* ── HERO ───────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 80% at 50% -20%, rgba(127,4,155,0.25) 0%, rgba(13,8,16,0) 60%)' }} />
        <div className="glow-orb w-96 h-96 opacity-15 top-1/4 -left-24" style={{ background: '#7f049b' }} />
        <div className="glow-orb w-64 h-64 opacity-10 bottom-1/4 -right-12" style={{ background: '#7f049b' }} />
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(127,4,155,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(127,4,155,0.5) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-24 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/30 bg-purple-500/10 mb-8">
            <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
            <span className="font-mono text-xs text-purple-300 tracking-widest uppercase">Premium Digital Agency</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-6xl md:text-8xl lg:text-9xl font-light leading-[0.9] text-white mb-6">
            We Build<br /><em className="accent-text not-italic">Digital</em><br />Excellence
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="font-body text-lg text-white/50 max-w-xl mx-auto leading-relaxed mb-10">
            From visionary startups to established enterprises — we craft premium digital experiences that captivate, convert, and endure.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/projects" className="btn-primary inline-flex items-center gap-2 text-base px-10 py-4">
              View Our Work <FiArrowRight />
            </Link>
            <Link to="/contact" className="btn-outline inline-flex items-center gap-2 text-base px-10 py-4">
              Start a Project
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-24 max-w-3xl mx-auto">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-display text-4xl font-light accent-text mb-1">{s.value}</div>
                <div className="font-body text-xs text-white/40 tracking-wide">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="font-mono text-xs text-white/20 tracking-widest">SCROLL</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
            className="w-px h-10 bg-gradient-to-b from-purple-500 to-transparent" />
        </motion.div>
      </section>

      {/* ── SERVICES PREVIEW ───────────────────────────────── */}
      <section className="py-32 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-3">
              What We Do
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="section-title">
              Services Built for<br /><em className="accent-text not-italic">Modern Brands</em>
            </motion.h2>
          </div>
          <Link to="/services" className="btn-outline inline-flex items-center gap-2 self-start md:self-auto">
            All Services <FiArrowUpRight />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass-card p-8 group hover:border-purple-500/50 transition-all duration-500 cursor-pointer"
              onClick={() => {
                if (s.externalLink) {
                  window.open(s.externalLink, '_blank', 'noopener,noreferrer');
                }
              }}
              style={s.externalLink ? { cursor: 'pointer' } : { cursor: 'default' }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                style={{ background: 'linear-gradient(135deg, rgba(127,4,155,0.3), rgba(28,12,25,0.5))' }}>
                <span className="text-purple-400">{s.icon}</span>
              </div>
              <h3 className="font-display text-xl font-medium text-white mb-3 flex items-center gap-2">
                {s.title}
                {s.externalLink && <FiArrowUpRight size={14} className="text-purple-400 opacity-70" />}
              </h3>
              <p className="font-body text-sm text-white/50 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── FEATURED PROJECTS ──────────────────────────────── */}
      <section className="py-32 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-3">
              Our Work
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="section-title">
              Featured <em className="accent-text not-italic">Projects</em>
            </motion.h2>
          </div>
          <Link to="/projects" className="btn-outline inline-flex items-center gap-2 self-start md:self-auto">
            View All <FiArrowUpRight />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
        </div>
      </section>

      {/* ── WHY CHOOSE US ──────────────────────────────────── */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(127,4,155,0.1) 0%, transparent 70%)' }} />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-3">
                Why  SitePixel
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="section-title mb-6">
                Crafted with<br /><em className="accent-text not-italic">Purpose</em>
              </motion.h2>
              <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                className="font-body text-white/50 leading-relaxed mb-10">
                We don't just build websites — we create digital systems that think, breathe, and grow with your brand. Every pixel is intentional, every interaction considered.
              </motion.p>
              <Link to="/about" className="btn-primary inline-flex items-center gap-2">
                Our Story <FiArrowRight />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { title: 'Strategic Thinking', desc: 'We align design with your business goals and user psychology.' },
                { title: 'Premium Quality', desc: 'No templates. Every project is crafted from scratch with care.' },
                { title: 'On-Time Delivery', desc: 'Reliable timelines and transparent communication throughout.' },
                { title: 'Ongoing Support', desc: "We're your long-term digital partner, not a one-time vendor." },
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass-card p-6">
                  <h4 className="font-display text-lg text-white mb-2">{item.title}</h4>
                  <p className="font-body text-xs text-white/40 leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ───────────────────────────────────── */}
      <section className="py-32 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-3">
            Testimonials
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="section-title">
            Clients Who <em className="accent-text not-italic">Love</em> Our Work
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass-card p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(t.stars)].map((_, j) => (
                  <FiStar key={j} className="text-yellow-400 fill-yellow-400" size={14} />
                ))}
              </div>
              <p className="font-body text-sm text-white/60 leading-relaxed mb-6 italic">"{t.text}"</p>
              <div>
                <p className="font-body text-sm font-medium text-white">{t.name}</p>
                <p className="font-body text-xs text-purple-400">{t.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <section className="py-32 max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="glass-card p-16 text-center relative overflow-hidden">
          <div className="glow-orb w-80 h-80 opacity-20 -top-20 left-1/2 -translate-x-1/2" style={{ background: '#7f049b' }} />
          <p className="font-mono text-xs text-purple-400 tracking-widest uppercase mb-4">Let's Work Together</p>
          <h2 className="font-display text-5xl md:text-7xl font-light text-white mb-6">
            Ready to Build<br /><em className="accent-text not-italic">Something Great?</em>
          </h2>
          <p className="font-body text-white/40 max-w-md mx-auto mb-10">
            Tell us about your project and we'll get back to you within 24 hours with a tailored proposal.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2 text-lg px-12 py-4">
            Start a Conversation <FiArrowRight />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;
